import { TrashRegularIcon } from "@elixir/icons";
import {
  DISCOUNT_TYPE,
  ERROR_CODE,
  RECEIPT_PAYMENT_MODE_ID,
  RECEIPT_TYPE,
  SUBJECT_KEYS,
  TCreateReceiptResponse,
  TGetReceiptPdfConfig,
} from "@elixir/types";
import { getAclViewClassName, printRx } from "@elixir/utils";
import { Modal, notification } from "antd";
import React, { useMemo, useState } from "react";
import usePostCreateReceipt from "../../apis/tryst/create-receipt";
import useGetQueueConfig from "../../apis/tryst/get-queue-config";
import useDeleteReceipt from "../../apis/tryst/use-delete-receipt";
import { ReactComponent as BoltRegularIcon } from "../../assets/icons/bolt-regular.svg";
import { ReactComponent as LoadingCircleIcon } from "../../assets/icons/custom/loading-circle.svg";
import { ReactComponent as PlusSolidIcon } from "../../assets/icons/font-awesome/plus-solid.svg";
import { ReactComponent as PenSolidIcon } from "../../assets/icons/pen-solid.svg";
import { ReactComponent as PrintRegularIcon } from "../../assets/icons/print-regular.svg";
import { ReactComponent as SendRegularIcon } from "../../assets/icons/send-paper-plane-top-regular.svg";
import useBillingStore from "../../stores/billing-store/billing-store";
import { TReceiptEntity } from "../../types/billing";
import {
  createNewReceipt,
  editReceipt,
  viewReceipt,
} from "../../utils/receipt";
import { serializeErrorForTracking, track } from "../../utils/tracking";
import TooltipComponent from "../common/tooltip-component";
import { sendBusinessReceiptWAMessage } from "apis";
import { useMyAccessProfileV2 } from "../../apis/tryst/get-my-access-profile";
import { massageMobileNumberForReceipt } from "../../utils/receipt-utils";
import { useReceiptStore } from "@elixir/stores";
import {
  generateReceiptPDFOnClient,
  PAGIFY_FAILURE_FALLBACK_STRING,
} from "../../receiptOnClient/GeneratePDFOnClient";
import uploadReceiptBlobV2 from "../../apis/parchi/put-receipt-blob-v2";
import { massagePatientProfileForReceipt } from "../../apis/tryst/receipt-v2/use-massage-receipt-data-and-config-for-pdf";
import { docListMassaged } from "../receipt-customisation/customisation-utls";
import { makeReceiptOnLocal } from "../../apis/tryst/receipt-v2/should-receipt-be-made-on-local-util";
import usePutUpdateReceipt from "../../apis/tryst/update-receipt";
import useCreateReceiptPdfV2 from "../../apis/tryst/create-receipt-pdf-v2-handler";
import getB from "../../utils/bridges/EkaBridge";
import queryClient from "../../instances/react-query-client";
import { getAllQueuesQuery } from "../../apis/tryst/get-all-queues";
import { getPatientDetails, isAccessAvailable, showAccessDeniedError } from "../../utils/bridges/methods";
import useGetAllAppointmentsV2 from "../../payment-dashboard-v2/api/useGetAllAppointmentsV2";
import { blockTimerForNSeconds } from "../../payment-dashboard-v2/utils/generic-utils";

export interface ReceiptV2ReturnResponseMassaged {
  success: boolean;
  data: TCreateReceiptResponse;
  meta: {
    headerText: string;
    pdfConfigForGeneration: TGetReceiptPdfConfig;
  };
  generatedFileName: string;
}
const QuickReceipt = ({
  isDisabled,
  isLoading,
  onQuickReceiptClick,
}: {
  isDisabled: boolean;
  isLoading: boolean;
  onQuickReceiptClick: () => void;
}) => {
  return (
    <div
      className={`border border-darwin-primary-500 bg-neutral-0 px-18 py-6 flex justify-center items-center rounded-4 space-x-4 w-full ${isDisabled ? "opacity-30" : "cursor-pointer"
        }`}
      onClick={onQuickReceiptClick}
    >
      {isLoading ? (
        <LoadingCircleIcon className="w-16 h-16 fill-neutral-2 animate-spin" />
      ) : (
        <>
          <BoltRegularIcon className="w-16 h-16 text-darwin-primary-500" />
          <div className="text-13 font-700 leading-6 text-darwin-primary-500">
            Quick Receipt
          </div>
        </>
      )}
    </div>
  );
};

const ActionCircle = ({
  disabled = false,
  IconComponent,
  onClick,
  tooltipText = "",
  className,
}: {
  disabled?: boolean;
  IconComponent: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  tooltipText?: string;
  className?: string;
}) => {
  return (
    <TooltipComponent title={tooltipText}>
      <div
        onClick={onClick}
        className={`bg-darwin-neutral-50 hover:bg-darwin-neutral-900 border border-darwin-neutral-100 p-6 rounded-full cursor-pointer action-icon-parent-class ${disabled ? "opacity-40 pointer-events-none" : ""
          } ${className ? className : ""}`}
      >
        <IconComponent
          className="w-16 h-16 text-darwin-neutral-900 action-icon-class"
          style={{}}
        />
      </div>
      <style>{`
        .action-icon-parent-class:hover > .action-icon-class {
            color: white;
        }
      `}</style>
    </TooltipComponent>
  );
};

const ReceiptActions = ({
  receiptEntity,
  isPaid,
  isQuickReceiptDisabled,
}: {
  receiptEntity: TReceiptEntity;
  isPaid: boolean;
  isQuickReceiptDisabled: boolean;
}) => {
  const { mutateAsync: postCreateReceipt, isLoading: isCreateReceiptLoading } =
    usePostCreateReceipt();

  const { mutateAsync: createReceiptPdfServer } = useCreateReceiptPdfV2();

  const { mutateAsync: putUpdateReceipt, isLoading: updateReceiptLoading } =
    usePutUpdateReceipt();

  const [isQuickReceiptProcessing, setIsQuickReceiptProcessing] =
    useState(false);

  const usersListEntire = docListMassaged();

  const activeDoctor = usersListEntire.doctors.find(
    (d) => d.oid === receiptEntity?.doctor_oid
  );
  const clinicInfo = activeDoctor?.clinics.find(
    (c) => c.id === receiptEntity.clinic_id
  );

  const {
    aid,
    clinic_id,
    patient,
    receipt_id,
    receiptSkus = [],
    paymentItems = [],
    receipt_url,
    receipt_type,
  } = receiptEntity;

  const currSkuItem = receiptSkus[0];

  const hasPrepaid = !!paymentItems.find(
    (item) => item.paymode === "EKA_PREPAID"
  );

  const paymentTimelineDuration = useBillingStore(
    (state) => state.paymentTimelineDuration
  );
  const clinicId = useBillingStore((state) => state.clinicId);

  const { data: billingQueue, refetch: refetchBillingQueue } =
    useGetAllAppointmentsV2({
      clinicId,
      startDate: paymentTimelineDuration.startDate,
      endDate: paymentTimelineDuration.endDate,
    });

  const { data: myAccessProfile } = useMyAccessProfileV2();
  const activeDoctorProfile = myAccessProfile?.allDocs.find(
    (d) => d.oid === receiptEntity?.doctor_oid
  );

  const setReceipt = useReceiptStore((state) => state.setReceipt);
  const receipt = useReceiptStore((state) => state.receipt);

  const currentAppointment = billingQueue?.response?.apt?.find(
    (item) => item.aid === aid
  );

  const activerClinic = myAccessProfile?.clinics.find(
    (c) => c.id === clinic_id
  );

  const handleQuickReceiptClick = async () => {
    const isCreateReceiptAllowed = isAccessAvailable(
      SUBJECT_KEYS.CAN_CREATE_RECEIPT_WITH_APT
    );
    if (!isCreateReceiptAllowed) {
      showAccessDeniedError();
      return;
    }

    track("BILLING_ACTION_SELECT", {
      receipt_id: receipt_id?.toString(),
      option: "QUICK_RECEIPT",
    });

    if (currSkuItem && !isQuickReceiptProcessing && !isQuickReceiptDisabled) {
      setIsQuickReceiptProcessing(true);
      let createdReceiptResponse: ReceiptV2ReturnResponseMassaged | undefined;
      try {
        createdReceiptResponse = (await postCreateReceipt({
          businessId: myAccessProfile?.business?.id!,
          doctor_oid: activeDoctorProfile?.oid,
          meta: {
            clinic_id,
            patient_oid: patient.oid || "",
            aid,
            receipt_type:
              currSkuItem.amount >= 0
                ? RECEIPT_TYPE.INCLINIC
                : RECEIPT_TYPE.REFUND,
          },
          payload: {
            remarks: "",
            amount_due: 0,
            name: `${patient.fln || patient.fn}`,
            mobile: (() => {
              // Construct full phone number with country code: priority is phone.code > ccd > default +91

              let fullPhoneNumber = patient?.mobile || "";

              // If mobile already has country code, use it as-is
              if (fullPhoneNumber && fullPhoneNumber.startsWith("+")) {
                return massageMobileNumberForReceipt(fullPhoneNumber);
              }

              // Try to get country code from patient object
              const countryCode =
                (patient as any)?.phone?.code || (patient as any)?.ccd || "+91";

              if (fullPhoneNumber) {
                fullPhoneNumber = `${countryCode}${fullPhoneNumber}`;
              }

              const finalMobile =
                massageMobileNumberForReceipt(fullPhoneNumber);
              return finalMobile;
            })(),
            receipt_amount: currSkuItem.amount,
            additional_discount_value: 0,
            additional_discount_type: DISCOUNT_TYPE.AMOUNT,
            additional_discount_amount: 0,
            net_amount: currSkuItem.amount,
            receipt_payments: [
              {
                amount: currSkuItem.amount,
                paymode:
                  paymentItems?.[0]?.paymode || RECEIPT_PAYMENT_MODE_ID.CASH,
              },
            ],
            receipt_skus: [
              {
                service_id: currSkuItem.sku_item.service_id || null,
                service_name: currSkuItem.sku_item.service_name.toLowerCase(),
                quantity: 1,
                price: currSkuItem.amount,
                amount: currSkuItem.amount,
                discount_type: DISCOUNT_TYPE.AMOUNT,
                discount_value: 0,
                discount_amount: 0,
                net_amount: currSkuItem.amount,
              },
            ],
          },
        })) as ReceiptV2ReturnResponseMassaged;
        const makeOnLocal = makeReceiptOnLocal();
        if (makeOnLocal) {
          const resOfPdf = await generateReceiptPDFOnClient({
            receiptData: createdReceiptResponse.data!,
            pdfConfigForGeneration: (createdReceiptResponse as any)?.meta
              ?.pdfConfigForGeneration!,
            patientData: massagePatientProfileForReceipt(
              receiptEntity.patient,
              currentAppointment!
            ),
            doctorProfile: {
              ...(activeDoctor || activeDoctorProfile)!,
              headerText: createdReceiptResponse?.meta?.headerText,
              clinicObject: clinicInfo || activerClinic,
            },
          });
          if (!resOfPdf.success) {
            track("QUICK_RECEIPT_PDF_GENERATION_FAILED", {
              on: "quick receipt flow",
              what: JSON.stringify(resOfPdf),
            });
            notification.error({
              message: "PDF generation failed",
              placement: "bottomRight",
              duration: 3,
            });
            return;
          }
          if (resOfPdf.success) {
            const uploadToS3Res = await uploadReceiptBlobV2({
              file: resOfPdf.blob!,
              fileName: createdReceiptResponse.generatedFileName,
            });

            if (!uploadToS3Res?.success) {
              track("QUICK_RECEIPT_PDF_S3_UPLOAD_FAILED", {
                on: "quick receipt flow",
                what: JSON.stringify(uploadToS3Res),
              });
              notification.error({
                message: "Uploading Receipt to server failed",
                placement: "bottomRight",
                duration: 3,
              });
              return;
            }

            if (uploadToS3Res?.success) {
              setReceipt({
                ...(receipt ?? {}),
                receipt_url: uploadToS3Res?.url,
              });
              notification.success({
                message: "Quick Receipt Created Successfully",
                placement: "bottomRight",
                duration: 3,
              });
              await blockTimerForNSeconds();
              refetchBillingQueue();
              return resOfPdf.blob;
            }
          }
        }
        notification.success({
          message: "Quick Receipt Created Successfully",
          placement: "bottomRight",
          duration: 3,
        });
        await blockTimerForNSeconds();
        refetchBillingQueue();
      } catch (err: any) {
        if (!createdReceiptResponse) {
          track("ERROR", {
            on: "masterERROR",
            what: serializeErrorForTracking(err),
          });
          window.alert("failed to create receipt");
          return;
        }
        if (err?.message.includes(PAGIFY_FAILURE_FALLBACK_STRING)) {
          track("ERROR", {
            on: createdReceiptResponse?.data?.receipt_id?.toString(),
            aid: createdReceiptResponse?.data?.aid || aid,
            what: serializeErrorForTracking(err),
            identifier: "QUICK_RECEIPT_PAGIFY_FAILED_SO_DEFAULT_BACK",
          });

          const pdfObject = (err as any)?.pdfObject;
          useReceiptStore.getState().setforceServerReceiptGeneration(true);

          try {
            const response = await createReceiptPdfServer({
              pdfObject,
            });

            if (response?.receipt_url) {
              notification.success({
                message: "Quick Receipt Created Successfully",
                placement: "bottomRight",
                duration: 3,
              });
              await blockTimerForNSeconds();
              refetchBillingQueue();
            } else {
              throw new Error("PDF generation failed");
            }
            useReceiptStore.getState().setforceServerReceiptGeneration(false);
          } catch (retryErr) {
            notification.error({
              message: "Failed to create receipt",
              placement: "bottomRight",
              duration: 3,
            });
          }
        }
      } finally {
        setIsQuickReceiptProcessing(false);
        useReceiptStore.getState().setforceServerReceiptGeneration(false);
      }
    }
  };
  const onReceiptSuccess = () => {
    (async () => {
      await blockTimerForNSeconds(1200);
      refetchBillingQueue();
    })();
  };

  const handleInitiateClick = () => {
    track("BILLING_ACTION_SELECT", {
      receipt_id: receipt_id?.toString(),
      option: "INITIATE",
    });

    const appointment = billingQueue?.response?.apt?.find(
      (item) => item.aid === aid
    );

    if (!appointment) {
      return;
    }

    createNewReceipt({ appointment, onReceiptSuccess });
  };

  const handleViewReceiptClick = () => {
    track("BILLING_ACTION_SELECT", {
      receipt_id: receipt_id?.toString(),
      option: "VIEW_RECEIPT",
    });

    const appointment = billingQueue?.response?.apt?.find(
      (item) => item.aid === aid
    );

    if (!appointment) {
      return;
    }

    const receipt = appointment.receipts.find(
      (item) => item.receipt_id == receipt_id
    );

    if (!receipt) {
      return;
    }

    viewReceipt({ receipt, appointment, onReceiptSuccess });
  };

  const handleEditReceiptClick = () => {
    track("BILLING_ACTION_SELECT", {
      receipt_id: receipt_id?.toString(),
      option: "EDIT",
    });

    const appointment = billingQueue?.response?.apt?.find(
      (item) => item.aid === aid
    );

    if (!appointment) {
      return;
    }

    editReceipt({ appointment, receiptId: receipt_id!, onReceiptSuccess });
  };

  const handleSendWAMessage = async () => {
    track("BILLING_ACTION_SELECT", {
      receipt_id: receipt_id?.toString(),
      option: "SEND_WA_MESSAGE",
    });

    if (receipt_id) {
      const response = await sendBusinessReceiptWAMessage({
        receiptId: receipt_id,
      });

      if (response.success) {
        if (
          response.error_meta &&
          response.error_meta.code === ERROR_CODE.LOW_CREDITS
        ) {
          notification.success({
            message: response.error_meta.message,
            placement: "bottomRight",
          });
        } else {
          notification.success({
            message: "Whatsapp message sent successfully",
            placement: "bottomRight",
            duration: 3,
          });
        }
      } else {
        notification.error({
          message: "Error sending whatsapp message. Please try again",
          placement: "bottomRight",
          duration: 3,
        });
      }
    }
  };

  const handleOnPrintClick = async () => {
    track("BILLING_ACTION_SELECT", {
      receipt_id: receipt_id?.toString(),
      option: "PRINT",
    });
    if (receipt_url) {
      printRx(receipt_url);
    }
  };

  const { mutateAsync: deleteArchiveReceipt } = useDeleteReceipt();

  const handleOnDeleteClick = async () => {
    const selectedAppointment = billingQueue?.response?.apt?.find(
      (item) => item.aid === aid
    );
    const selectedReceipt = selectedAppointment?.receipts?.find(
      (item) => item.receipt_id === receipt_id
    );

    if (!selectedReceipt) {
      return;
    }

    Modal.confirm({
      title: "Are you sure you want to delete the receipt?",
      content: selectedReceipt.amount_due
        ? `This Receipt has a due of ₹${selectedReceipt.amount_due}. ${selectedAppointment?.patient?.fn
        }'s current outstanding due is ₹${selectedAppointment?.patient?.amount_pending || 0
        }. After receipt is deleted, outstanding due amount would be ₹${Math.max(
          (selectedAppointment?.patient?.amount_pending || 0) -
          selectedReceipt.amount_due,
          0
        )}`
        : undefined,
      okText: "Yes",
      onOk: async () => {
        const patientOid = patient?.oid || selectedAppointment?.patient_oid;
        let abhaId: string | undefined;
        if (patientOid) {
          const patients = await getPatientDetails({
            patientOids: [patientOid],
          });
          abhaId = patients?.[0]?.patient?.abha?.id;
        }
        const { success } = await deleteArchiveReceipt({
          receiptId: selectedReceipt.receipt_id,
          patientOid,
          abhaId,
        });

        if (success) {
          notification.success({
            message: "Deleted Receipt Successfully",
            placement: "bottomRight",
          });

          await blockTimerForNSeconds();
          refetchBillingQueue();
          await blockTimerForNSeconds();
          const bridge = await getB();
          await bridge.fetchPatientListFromServer([
            patient?.oid || selectedAppointment?.patient_oid,
          ]);
          queryClient.invalidateQueries(getAllQueuesQuery({}));
        } else {
          notification.error({
            message: "Failed to Delete Receipt",
            placement: "bottomRight",
          });
        }
      },
    });
  };

  const { data: queueConfigData } = useGetQueueConfig();
  const { data } = queueConfigData || {};
  const { is_edit_receipt_restricted } = data || {};

  const canShowDelete = useMemo(() => {
    const selectedAppointment = billingQueue?.response?.apt?.find(
      (item) => item.aid === aid
    );
    const selectedReceipt = selectedAppointment?.receipts?.find(
      (item) => item.receipt_id === receipt_id
    );

    if (!selectedReceipt) {
      return false;
    }

    const hasPrepaidOrPostpaid = !!selectedReceipt?.receipt_payment?.find(
      (item) =>
        item.paymode === "EKA_PREPAID" ||
        item.paymode === RECEIPT_PAYMENT_MODE_ID.EKA_POSTPAY
    );

    const showDeleteOption =
      !hasPrepaidOrPostpaid &&
      receipt_type !== RECEIPT_TYPE.DUE_PAYMENT &&
      !(
        selectedReceipt.payment_id ||
        selectedReceipt.payment_status === "created"
      );

    return showDeleteOption;
  }, [aid, billingQueue?.response?.apt, receipt_id, receipt_type]);

  return (
    <div className="grid grid-cols-3 divide-x divide-darwin-neutral-100 w-full">
      {isPaid ? (
        <>
          <div className="col-start-1 col-end-3 space-x-12 flex justify-between items-center pr-16">
            <ActionCircle
              disabled={
                is_edit_receipt_restricted || receipt_type === "DUE_PAYMENT"
              }
              IconComponent={hasPrepaid ? PlusSolidIcon : PenSolidIcon}
              onClick={
                hasPrepaid ? handleInitiateClick : handleEditReceiptClick
              }
              tooltipText={
                receipt_type === "DUE_PAYMENT"
                  ? "Payment Notes Cannot be edited"
                  : hasPrepaid
                    ? "Create"
                    : is_edit_receipt_restricted
                      ? "Staff Restricted"
                      : "Edit"
              }
              className={!hasPrepaid ? getAclViewClassName(SUBJECT_KEYS.RECEIPT_EDIT) : ""}
            />
            <ActionCircle
              onClick={handleSendWAMessage}
              IconComponent={SendRegularIcon}
              tooltipText="Send Message"
            />
            <ActionCircle
              onClick={handleOnPrintClick}
              IconComponent={PrintRegularIcon}
              tooltipText="Print Receipt"
            />
            <ActionCircle
              onClick={handleOnDeleteClick}
              IconComponent={TrashRegularIcon}
              tooltipText="Delete Receipt"
              disabled={!canShowDelete}
              className={getAclViewClassName(SUBJECT_KEYS.DELETE_RECEIPT)}
            />
          </div>
          <div
            className="col-start-3 col-end-4 flex justify-between items-center underline text-13 leading-5 font-700 text-actionPrimary-hover cursor-pointer pl-16"
            onClick={handleViewReceiptClick}
          >
            View Receipt
          </div>
        </>
      ) : (
        <>
          <div className="col-start-1 col-end-3 pr-16">
            <QuickReceipt
              isLoading={isQuickReceiptProcessing || isCreateReceiptLoading}
              onQuickReceiptClick={handleQuickReceiptClick}
              isDisabled={isQuickReceiptDisabled}
            />
          </div>
          <div
            className="col-start-3 col-end-4 flex items-center pl-16 underline text-13 leading-5 font-700 text-actionPrimary-hover cursor-pointer"
            onClick={handleInitiateClick}
          >
            Full Receipt
          </div>
        </>
      )}
    </div>
  );
};

export default ReceiptActions;
